<template>
  <div
    class="order-card bg-white border border-outline-color"
    @click="onClickCard"
  >
    <div class="order-card-date d-flex justify-content-between">
      <div class="d-flex">
        <p class="pr-3 d-flex align-items-center text-size-18 font-weight-bold">
          <FeatherIcon icon="calendar" size="1x" class="mr-1" />
          {{ getFormattedDate(order) }}
        </p>
        <p
          class="d-flex align-items-center text-size-18 font-weight-bold"
          data-testid="work-time-range"
        >
          <FeatherIcon icon="clock" size="1x" class="mr-1" />
          {{ formattedWorkTimeRange }}
        </p>
        <p
          v-if="isWorkFixed"
          class="d-flex align-items-end text-size-10 pl-1"
          data-testid="work-fixed-label"
        >
          [確定]
        </p>
      </div>
    </div>

    <template v-if="!isSimpleDisplay">
      <p class="text-size-18 py-1">
        {{ order.office.name }}
      </p>
      <p class="text-size-14 py-1">
        {{ order.address }}
      </p>
      <p class="text-size-12 py-1">
        {{ order.title }}
      </p>
    </template>

    <template v-if="!isAppliedPage && isSimpleDisplay">
      <p class="py-1">
        <span class="d-flex w-100 text-size-12">
          <FeatherIcon
            icon="map-pin"
            size="14"
            class="flex-shrink-0 map-icon mr-1"
          />
          <span class="d-inline-block">{{ order.address }}</span>
        </span>
        <span class="d-flex w-100 mt-2 text-size-12">
          <FeatherIcon
            icon="navigation"
            size="14"
            class="flex-shrink-0 map-icon mr-1"
          />
          <span class="d-inline-block">{{ order.access }}</span>
        </span>
      </p>
    </template>
    <div class="d-flex justify-content-between align-items-end pt-1">
      <div class="d-flex align-items-end">
        <p class="text-red" data-testid="salary-per-day">
          {{ salaryPerDayWithVariousAllowances }}円
        </p>
        <p
          v-if="isWorkFixed"
          class="text-size-10 text-mild pl-1"
          data-testid="work-fixed-label"
        >
          [確定]
        </p>
      </div>

      <BBadge
        v-if="badgeAttr"
        :variant="badgeAttr.bgColor"
        class="text-size-14 text-weight-400 p-2"
        :class="`text-${badgeAttr.color}`"
      >
        {{ badgeAttr.text }}
      </BBadge>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { relativeTime } from "@/utils/relativeTime";
import { Application } from "@/types/order/Application";
import applicationTab from "@/models/applicationTab";
import { OrderDetail } from "@/types/order/Order";
import { orderService } from "@/app/services/OrderService";
import { applicationService } from "@/app/services/ApplicationService";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { formatFromISOString, parseISO } from "@/utils/date";

@Component({
  components: {
    FeatherIcon
  }
})
export default class ApplicationCard extends Vue {
  @Prop({
    required: true,
    type: Object
  })
  application!: Application;

  @Prop({
    type: Boolean,
    default: false
  })
  isSimpleDisplay!: boolean;

  get order() {
    return this.application.order;
  }

  get date() {
    return formatFromISOString(this.application.appliedStartWorkAt, "M月d日");
  }

  get badgeAttr() {
    switch (this.application.status) {
      case "applied": {
        const time = relativeTime(
          parseISO(this.application.appliedStartWorkAt)
        );
        return {
          text: time === "いま" ? "勤務開始済" : `${time}に勤務開始`,
          bgColor: "red",
          color: "white"
        };
      }
      case "doing": {
        if (this.application.attendanceCorrection) {
          return {
            text: "勤務中(遅刻有)",
            bgColor: "green",
            color: "white"
          };
        }
        return {
          text: "勤務中",
          bgColor: "green",
          color: "white"
        };
      }
      case "done": {
        if (this.application.attendanceCorrection) {
          let text = "勤怠変更未申請";
          let bgColor = "red";
          let color = "white";
          switch (this.application.attendanceCorrection.status) {
            case "submitted":
              text = "勤怠変更申請中";
              bgColor = "gray-02";
              color = "primary-dark";
              break;
            case "rejected":
              text = "勤怠変更却下";
              break;
            case "approved":
              text = "評価まち(勤怠変更あり)";
              bgColor = "yellow";
              color = "white";
              break;
          }

          return {
            text,
            bgColor,
            color
          };
        } else {
          return {
            text: "評価まち",
            bgColor: "yellow",
            color: "white"
          };
        }
      }
      case "requested_cancel": {
        return {
          text: "キャンセル済み",
          bgColor: "yellow",
          color: "white"
        };
      }
      case "canceled": {
        return {
          text: "キャンセル済み",
          bgColor: "gray-02",
          color: "gray-05"
        };
      }
      case "evaluated": {
        return {
          text: "事業所を評価済み",
          bgColor: "gray-02",
          color: "primary-dark"
        };
      }
      default: {
        return null;
      }
    }
  }

  get isWorkFixed(): boolean {
    return (
      this.application.attendanceCorrection?.status === "approved" ||
      (["done", "evaluated"].includes(this.application.status) &&
        this.application.attendanceCorrection === null)
    );
  }

  get salaryPerDayWithVariousAllowances(): string {
    if (this.application.attendanceCorrection?.status === "approved") {
      return applicationService
        .getCorrectedSalaryPerDayWithVariousAllowances(this.application)
        .toLocaleString();
    }
    return applicationService
      .getSalaryPerDayWithVariousAllowances(this.application)
      .toLocaleString();
  }

  get formattedWorkTimeRange(): string {
    if (this.application.attendanceCorrection?.status === "approved") {
      return applicationService.getFormattedCorrectedWorkTimeRange(
        this.application
      );
    }
    return applicationService.getFormattedWorkTimeRange(this.application);
  }

  getFormattedDate(order: OrderDetail): string {
    return orderService.getFormattedDateWithSlash(order);
  }

  onClickCard() {
    this.$emit("click");
  }

  relativeTime(d: number) {
    return relativeTime(d);
  }

  toLocaleString(v: number) {
    return v.toLocaleString();
  }

  get isAppliedPage() {
    return this.$route.query.tab === applicationTab.APPLIED;
  }
}
</script>

<style lang="scss" scoped>
.order-card {
  padding: 16px 16px 12px;
  border-radius: 4px;

  &-date {
    padding-bottom: 6px;
  }

  &-office {
    padding-right: 6px;
  }

  .map-icon {
    margin-top: 2px;
  }
}
</style>
